export function isValidSupplierId(supplierId: unknown): boolean {
    if (supplierId == null || supplierId === "") {
        return false;
    }
    const id = Number(supplierId);
    return Number.isFinite(id) && id > 0;
}

export function filterVariantsWithValidSupplier<
    T extends { supplierId?: unknown },
>(variants: T[]): T[] {
    return variants.filter((variant) => isValidSupplierId(variant.supplierId));
}

export interface OfferVariantShape {
    id?: string | number;
    supplierId?: unknown;
    priceWithTax?: number;
    qty?: number | null;
    supplyType?: string | null;
}

export function isSellableOffer(
    variant: OfferVariantShape | null | undefined,
    options?: { requirePositivePrice?: boolean }
): boolean {
    if (!variant || !isValidSupplierId(variant.supplierId)) {
        return false;
    }
    const requirePositivePrice = options?.requirePositivePrice ?? true;
    if (requirePositivePrice && (variant.priceWithTax ?? 0) <= 0) {
        return false;
    }
    const supplyType = (variant.supplyType ?? "").toLowerCase();
    if (supplyType === "pre-order") {
        return true;
    }
    return (variant.qty ?? 0) > 0;
}

export function pickCheapestOffer<T extends OfferVariantShape>(
    variants: T[],
    preferred?: T | null
): T | undefined {
    const list = filterVariantsWithValidSupplier(variants);
    const sellable = list.filter((variant) => isSellableOffer(variant));
    if (sellable.length > 0) {
        return [...sellable].sort(
            (a, b) => (a.priceWithTax ?? 0) - (b.priceWithTax ?? 0)
        )[0];
    }
    if (preferred && isSellableOffer(preferred)) {
        return preferred;
    }
    return undefined;
}

export function hasDisplayableOffers(
    product: {
        variants?: OfferVariantShape[] | null;
        cheapestVariant?: OfferVariantShape | null;
    },
    options?: { requirePositivePrice?: boolean }
): boolean {
    const variants = filterVariantsWithValidSupplier(product.variants ?? []);

    if (
        product.cheapestVariant &&
        isSellableOffer(product.cheapestVariant, options)
    ) {
        return true;
    }

    return variants.some((variant) => isSellableOffer(variant, options));
}

export function hasSellableOffer(product: {
    variants?: OfferVariantShape[] | null;
    cheapestVariant?: OfferVariantShape | null;
}): boolean {
    return hasDisplayableOffers(product);
}
